/**
 * Push notifications on the phone (Capacitor), for Beu Messages. The CTO adds
 * the native plugin (@capacitor/push-notifications) to the app shells and
 * sends the pushes; this file only talks to it, so the website needs no
 * package: registerPlugin reaches the plugin in the shell when it's there.
 *
 *   pushAvailable()     on a phone with the plugin (or the demo, below)
 *   permission()        'granted' | 'denied' | 'prompt' | 'unsupported'
 *   turnOn()            asks the phone, then registers for a token
 *   listen({ onToken, onTap, onShown })  the plugin's events
 *   clearDelivered(id)  remove a chat's notifications once you've opened it
 *
 * Demo (local, dev and staging only): localStorage 'bm.pushDemo' = '1' acts as
 * a phone, so the turn-on card and settings can be tried in a browser.
 */
import { Capacitor, registerPlugin } from '@capacitor/core';

const Native = registerPlugin('PushNotifications');
const DEMO_KEY = 'bm.pushDemo';
const DEMO_STATE = 'bm.pushDemoState';

function demo() {
    if (String(process.env.REACT_APP_ENV || '').toLowerCase() === 'prod') return false;
    try { return localStorage.getItem(DEMO_KEY) === '1'; } catch (e) { return false; }
}

function native() {
    try { return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('PushNotifications'); } catch (e) { return false; }
}

export function pushAvailable() { return native() || demo(); }

export function platform() {
    try { return Capacitor.getPlatform(); } catch (e) { return 'web'; }
}

const asState = (r) => {
    const v = r && (r.receive || r.state);
    if (v === 'granted') return 'granted';
    if (v === 'denied') return 'denied';
    return 'prompt';
};

export async function permission() {
    if (demo()) { try { return localStorage.getItem(DEMO_STATE) || 'prompt'; } catch (e) { return 'prompt'; } }
    if (!native()) return 'unsupported';
    try { return asState(await Native.checkPermissions()); } catch (e) { return 'unsupported'; }
}

/** Ask the phone, then register: the token arrives through listen({ onToken }). */
export async function turnOn() {
    if (demo()) {
        try { localStorage.setItem(DEMO_STATE, 'granted'); } catch (e) { /* ignore */ }
        demoTokenListeners.forEach((fn) => fn('demo-token', 'web'));
        return 'granted';
    }
    if (!native()) return 'unsupported';
    const state = asState(await Native.requestPermissions());
    if (state === 'granted') await Native.register();
    return state;
}

const demoTokenListeners = new Set();

/**
 * The plugin's events. onTap(data) when a notification is tapped; onShown(data)
 * when one arrives while the app is open (phones don't show those themselves).
 * Returns a function that stops listening.
 */
export function listen({ onToken, onTap, onShown } = {}) {
    if (demo()) {
        if (onToken) demoTokenListeners.add(onToken);
        // Try a tap in the demo: window.dispatchEvent(new CustomEvent('bm:push-tap', { detail: { space, conversationId } }))
        const tap = (e) => onTap && onTap((e && e.detail) || {});
        window.addEventListener('bm:push-tap', tap);
        return () => { demoTokenListeners.delete(onToken); window.removeEventListener('bm:push-tap', tap); };
    }
    if (!native()) return () => {};
    const handles = [];
    const add = (name, fn) => { Promise.resolve(Native.addListener(name, fn)).then((h) => handles.push(h)).catch(() => {}); };
    if (onToken) add('registration', (t) => onToken(t && t.value, platform()));
    if (onTap) add('pushNotificationActionPerformed', (a) => onTap((a && a.notification && a.notification.data) || {}));
    if (onShown) add('pushNotificationReceived', (n) => onShown((n && n.data) || {}, n));
    return () => handles.forEach((h) => { try { h.remove(); } catch (e) { /* ignore */ } });
}

/** Opening a chat clears its notifications from the phone's list. */
export async function clearDelivered(conversationId) {
    if (!conversationId || !native()) return;
    try {
        const list = await Native.getDeliveredNotifications();
        const mine = ((list && list.notifications) || []).filter((n) => n && n.data && String(n.data.conversationId) === String(conversationId));
        if (mine.length) await Native.removeDeliveredNotifications({ notifications: mine });
    } catch (e) { /* older shells: nothing to clear */ }
}
